import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface IPWhitelistEntry {
  id: number;
  ip_address: string;
  reason: string;
  created_by: number;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

interface RateLimitStats {
  whitelistedIPs: number;
  activeRateLimits: number;
  blockedIPs: number;
}

interface RateLimitStatus {
  ip: string;
  blocked: boolean;
  whitelisted: boolean;
  entries: Array<{
    ip: string;
    count: number;
    resetTime: number;
    blockedUntil?: number;
  }>;
}

const RateLimitPage: React.FC = () => {
  const [whitelist, setWhitelist] = useState<IPWhitelistEntry[]>([]);
  const [stats, setStats] = useState<RateLimitStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIP, setSelectedIP] = useState<string>('');
  const [ipStatus, setIpStatus] = useState<RateLimitStatus | null>(null);

  // Form states
  const [newIP, setNewIP] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [whitelistRes, statsRes] = await Promise.all([
        api.get('/admin/rate-limit/whitelist'),
        api.get('/admin/rate-limit/stats')
      ]);

      setWhitelist(whitelistRes.data.data);
      setStats(statsRes.data.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddIP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newIP || !reason) {
      alert('IP e motivo são obrigatórios');
      return;
    }

    try {
      await api.post('/admin/rate-limit/whitelist', {
        ip_address: newIP,
        reason,
        expires_at: expiresAt || undefined
      });

      setNewIP('');
      setReason('');
      setExpiresAt('');
      setShowAddModal(false);
      loadData();
      alert('IP adicionado à whitelist com sucesso!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao adicionar IP');
    }
  };

  const handleRemoveIP = async (ip: string) => {
    if (!window.confirm(`Tem certeza que deseja remover o IP ${ip} da whitelist?`)) {
      return;
    }

    try {
      await api.delete(`/admin/rate-limit/whitelist/${ip}`);
      loadData();
      alert('IP removido da whitelist com sucesso!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao remover IP');
    }
  };

  const handleCheckIP = async () => {
    if (!selectedIP) {
      alert('Digite um IP para verificar');
      return;
    }

    try {
      const response = await api.get(`/admin/rate-limit/status/${selectedIP}`);
      setIpStatus(response.data.data);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao verificar IP');
    }
  };

  const handleClearRateLimit = async (ip: string) => {
    if (!window.confirm(`Limpar dados de rate limiting para o IP ${ip}?`)) {
      return;
    }

    try {
      await api.post(`/admin/rate-limit/clear/${ip}`);
      alert('Dados de rate limiting limpos com sucesso!');
      if (ip === selectedIP) {
        handleCheckIP(); // Refresh status
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao limpar dados');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Gerenciamento de Rate Limiting
        </h1>
        <p className="text-gray-600">
          Gerencie whitelist de IPs e monitore rate limiting do sistema
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">IPs Liberados</p>
                <p className="text-2xl font-bold text-gray-900">{stats.whitelistedIPs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Rate Limits Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeRateLimits}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">IPs Bloqueados</p>
                <p className="text-2xl font-bold text-gray-900">{stats.blockedIPs}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* IP Whitelist Management */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                Whitelist de IPs
              </h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Adicionar IP
              </button>
            </div>
          </div>

          <div className="p-6">
            {whitelist.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Nenhum IP na whitelist
              </p>
            ) : (
              <div className="space-y-4">
                {whitelist.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="font-mono text-lg font-semibold text-gray-900">
                          {entry.ip_address}
                        </span>
                        {isExpired(entry.expires_at) && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                            Expirado
                          </span>
                        )}
                        {!entry.is_active && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                            Inativo
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{entry.reason}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>Por: {entry.user?.name || 'Sistema'}</span>
                        <span>Criado: {formatDate(entry.created_at)}</span>
                        {entry.expires_at && (
                          <span>Expira: {formatDate(entry.expires_at)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveIP(entry.ip_address)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title="Remover da whitelist"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* IP Status Checker */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Verificar Status de IP
            </h2>
          </div>

          <div className="p-6">
            <div className="flex space-x-2 mb-6">
              <input
                type="text"
                value={selectedIP}
                onChange={(e) => setSelectedIP(e.target.value)}
                placeholder="Digite um endereço IP"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleCheckIP}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Verificar
              </button>
            </div>

            {ipStatus && (
              <div className="space-y-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Status do IP: {ipStatus.ip}
                  </h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${ipStatus.whitelisted ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className="text-sm">
                        {ipStatus.whitelisted ? 'Liberado' : 'Não liberado'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${ipStatus.blocked ? 'bg-red-500' : 'bg-green-500'}`}></div>
                      <span className="text-sm">
                        {ipStatus.blocked ? 'Bloqueado' : 'Não bloqueado'}
                      </span>
                    </div>
                  </div>

                  {ipStatus.entries.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Rate Limiting:</h4>
                      <div className="space-y-2">
                        {ipStatus.entries.map((entry, index) => (
                          <div key={index} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                            <div>Endpoint: {entry.ip}</div>
                            <div>Contagem: {entry.count}</div>
                            <div>Reset: {new Date(entry.resetTime).toLocaleString('pt-BR')}</div>
                            {entry.blockedUntil && (
                              <div className="text-red-600">
                                Bloqueado até: {new Date(entry.blockedUntil).toLocaleString('pt-BR')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex space-x-2">
                    {!ipStatus.whitelisted && (
                      <button
                        onClick={() => {
                          setNewIP(ipStatus.ip);
                          setReason('Liberado via verificação de status');
                          setShowAddModal(true);
                        }}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                      >
                        Liberar IP
                      </button>
                    )}
                    {ipStatus.entries.length > 0 && (
                      <button
                        onClick={() => handleClearRateLimit(ipStatus.ip)}
                        className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700 transition-colors"
                      >
                        Limpar Rate Limit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add IP Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Adicionar IP à Whitelist
            </h3>

            <form onSubmit={handleAddIP}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço IP
                  </label>
                  <input
                    type="text"
                    value={newIP}
                    onChange={(e) => setNewIP(e.target.value)}
                    placeholder="192.168.1.100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Descreva o motivo para liberar este IP"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Expiração (opcional)
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateLimitPage;